import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Card, Form, Input, message, Modal, Select, Space, Spin, Table, Tag, Tooltip, Typography,
} from 'antd';
import { Building2, ExternalLink, Phone, RefreshCw, RefreshCcw, UserPlus } from 'lucide-react';
import { particularsApi, usersApi, leadsApi, propertiesApi } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

const { Text } = Typography;

interface Agent { id: string; name: string; email: string; }
interface PrivateListing {
  id: string;
  propertyCode: string;
  runId: string;
  title?: string;
  price?: number;
  size?: number;
  pricePerSqm?: number;
  rooms?: number;
  bathrooms?: number;
  address?: string;
  district?: string;
  neighborhood?: string;
  municipality?: string;
  propertyType?: string;
  floor?: string;
  exterior?: boolean;
  hasLift?: boolean;
  url?: string;
  thumbnail?: string;
  propertyStatus?: string;
  contactName?: string;
  contactPhone?: string;
  contactPhoneRaw?: string;
  assignedToId?: string | null;
  assignedTo?: Agent | null;
  syncedAt: string;
}

export default function AvmPrivateListingsTab() {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [rows, setRows]         = useState<PrivateListing[]>([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [syncing, setSyncing]   = useState(false);
  const [search, setSearch]     = useState('');
  const [runId, setRunId]       = useState('');
  const [agents, setAgents]       = useState<Agent[]>([]);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [leadModal, setLeadModal] = useState<PrivateListing | null>(null);
  const [leadForm] = Form.useForm();
  const [creatingLead, setCreatingLead] = useState(false);
  const [creatingPropertyId, setCreatingPropertyId] = useState<string | null>(null);

  const fetchRows = useCallback(async (p = page, q = search) => {
    setLoading(true);
    try {
      const { data } = await particularsApi.list({ page: p, limit: 50, search: q || undefined });
      setRows(data.data || []);
      setTotal(data.meta?.total || 0);
    } catch {
      message.error('No se pudo cargar el listado');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchRows(1, ''); }, []);

  // Load agents for assignment dropdown
  useEffect(() => {
    usersApi.agents().then(({ data }) => setAgents(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const handleSync = async () => {
    if (!runId.trim()) {
      message.warning('Introduce el Run ID de Apify');
      return;
    }
    setSyncing(true);
    try {
      const { data } = await particularsApi.sync(runId.trim());
      message.success(
        `Sincronizado — Total items: ${data.total} · Particulares: ${data.particulares} · Guardados: ${data.upserted}`,
      );
      setPage(1);
      fetchRows(1, search);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al sincronizar';
      message.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const handleAssign = async (id: string, userId: string | null) => {
    setAssigning(id);
    try {
      await particularsApi.assign(id, userId);
      setRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, assignedToId: userId, assignedTo: agents.find((a) => a.id === userId) ?? null }
            : r,
        ),
      );
      message.success(userId ? 'Asignado correctamente' : 'Asignación eliminada');
    } catch {
      message.error('Error al asignar');
    } finally {
      setAssigning(null);
    }
  };

  const openLeadModal = (r: PrivateListing) => {
    setLeadModal(r);
    leadForm.setFieldsValue({
      firstName: r.contactName?.split(' ')[0] || r.contactName || '',
      lastName: r.contactName?.split(' ').slice(1).join(' ') || '',
      phone: r.contactPhoneRaw || r.contactPhone || '',
      notes: [
        r.address ? `Dirección: ${r.address}` : '',
        r.municipality ? `Municipio: ${r.municipality}` : '',
        r.neighborhood ? `Barrio: ${r.neighborhood}` : '',
        r.price ? `Precio anuncio: ${r.price.toLocaleString('es-ES')} €` : '',
        r.size ? `Superficie: ${r.size} m²` : '',
        r.rooms ? `Habitaciones: ${r.rooms}` : '',
        r.url ? `Idealista: ${r.url}` : '',
      ].filter(Boolean).join('\n'),
    });
  };

  const handleCreateLead = async () => {
    if (!leadModal) return;
    try {
      const values = await leadForm.validateFields();
      setCreatingLead(true);
      await leadsApi.create({
        firstName: values.firstName,
        lastName: values.lastName || undefined,
        phone: values.phone || undefined,
        source: 'listado-particulares',
        notes: values.notes || undefined,
        tags: 'particular,idealista',
      });
      message.success('Lead creado correctamente');
      setLeadModal(null);
      leadForm.resetFields();
    } catch (err: any) {
      if (!err?.errorFields) message.error('Error al crear el lead');
    } finally {
      setCreatingLead(false);
    }
  };

  const handleCreateProperty = async (row: PrivateListing) => {
    try {
      setCreatingPropertyId(row.id);
      const { data } = await propertiesApi.fromPrivateListing(row.id);
      message.success('Propiedad creada (sin publicar)');
      navigate(`/properties/${data.id}`);
    } catch {
      message.error('No se pudo crear la propiedad');
    } finally {
      setCreatingPropertyId(null);
    }
  };

  const columns = [
    {
      title: 'Contacto',
      key: 'contacto',
      width: 180,
      render: (_: any, r: PrivateListing) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{r.contactName || '—'}</Text>
          {r.contactPhone && (
            <div>
              <a
                href={`tel:${r.contactPhoneRaw || r.contactPhone}`}
                style={{ fontSize: 12, color: '#1677ff', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <Phone size={11} />
                {r.contactPhone}
              </a>
            </div>
          )}
          {r.url && (
            <div style={{ marginTop: 2 }}>
              <a
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 3 }}
              >
                <ExternalLink size={10} />
                Ver en Idealista
              </a>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Propiedad',
      key: 'propiedad',
      render: (_: any, r: PrivateListing) => (
        <div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>
            {r.municipality}{r.neighborhood ? ` · ${r.neighborhood}` : ''}
          </div>
          <Text style={{ fontSize: 13 }}>{r.address || r.title || '—'}</Text>
          <div style={{ marginTop: 3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {r.propertyType && <Tag style={{ fontSize: 11 }}>{r.propertyType}</Tag>}
            {r.floor && <Tag style={{ fontSize: 11 }}>Planta {r.floor}</Tag>}
            {r.exterior && <Tag color="blue" style={{ fontSize: 11 }}>Exterior</Tag>}
            {r.hasLift && <Tag style={{ fontSize: 11 }}>Ascensor</Tag>}
          </div>
        </div>
      ),
    },
    {
      title: 'Precio',
      key: 'precio',
      width: 130,
      render: (_: any, r: PrivateListing) => (
        <div>
          {r.price != null && (
            <Text strong style={{ fontSize: 14 }}>
              {r.price.toLocaleString('es-ES')} €
            </Text>
          )}
          {r.pricePerSqm != null && (
            <div style={{ fontSize: 11, color: '#9ca3af' }}>
              {Math.round(r.pricePerSqm).toLocaleString('es-ES')} €/m²
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Características',
      key: 'chars',
      width: 130,
      render: (_: any, r: PrivateListing) => (
        <div style={{ fontSize: 12, color: '#374151' }}>
          {r.size != null && <div>{r.size} m²</div>}
          {r.rooms != null && <div>{r.rooms} hab · {r.bathrooms ?? '?'} baños</div>}
          {r.propertyStatus && (
            <Tag
              color={r.propertyStatus === 'good' ? 'green' : r.propertyStatus === 'renew' ? 'orange' : 'default'}
              style={{ fontSize: 10, marginTop: 2 }}
            >
              {r.propertyStatus}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Enlace',
      key: 'enlace',
      width: 70,
      render: (_: any, r: PrivateListing) =>
        r.url ? (
          <Tooltip title="Ver en Idealista">
            <a href={r.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} style={{ color: '#1677ff' }} />
            </a>
          </Tooltip>
        ) : '—',
    },
    ...(isAdmin() ? [{
      title: 'Asignado a',
      key: 'asignado',
      width: 150,
      render: (_: any, r: PrivateListing) => (
        <Select
          size="small"
          allowClear
          placeholder="Sin asignar"
          value={r.assignedToId ?? undefined}
          loading={assigning === r.id}
          onChange={(val: string | undefined) => handleAssign(r.id, val ?? null)}
          style={{ width: '100%' }}
          options={agents.map((a) => ({ value: a.id, label: a.name }))}
        />
      ),
    }] : []),
    {
      title: 'Sincronizado',
      dataIndex: 'syncedAt',
      key: 'syncedAt',
      width: 100,
      render: (v: string) =>
        new Date(v).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 118,
      fixed: 'right' as const,
      render: (_: any, r: PrivateListing) => (
        <Space size={4} direction="vertical" style={{ width: '100%' }}>
          <Tooltip title="Crear lead con estos datos">
            <Button
              size="small"
              block
              icon={<UserPlus size={13} />}
              onClick={() => openLeadModal(r)}
            >
              Lead
            </Button>
          </Tooltip>
          <Tooltip title="Crea una propiedad Swomey sin publicar, copiando datos e imágenes">
            <Button
              size="small"
              block
              type="primary"
              ghost
              icon={<Building2 size={13} />}
              loading={creatingPropertyId === r.id}
              onClick={() => handleCreateProperty(r)}
            >
              Crear prop.
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      {/* Sync bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Input
              placeholder="Run ID de Apify (ej. 0KMHzddt9mUwbtvYJ)"
              value={runId}
              onChange={(e) => setRunId(e.target.value)}
              style={{ width: 280 }}
              onPressEnter={handleSync}
            />
            <Button
              type="primary"
              icon={<RefreshCcw size={13} />}
              loading={syncing}
              onClick={handleSync}
            >
              Sincronizar listado
            </Button>
          </Space>
          <Space>
            <Input.Search
              allowClear
              placeholder="Buscar por nombre, teléfono, zona…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={(v) => { setPage(1); fetchRows(1, v); }}
              style={{ width: 260 }}
            />
            <Button icon={<RefreshCw size={13} />} onClick={() => fetchRows(page, search)}>
              Recargar
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {total} particulares
            </Text>
          </Space>
        </Space>
      </Card>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        size="small"
        scroll={{ x: 980 }}
        pagination={{
          current: page,
          pageSize: 50,
          total,
          onChange: (p) => { setPage(p); fetchRows(p, search); },
          showTotal: (t) => `${t} registros`,
        }}
        expandable={{
          expandedRowRender: (r: PrivateListing) => (
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              {r.thumbnail && (
                <img
                  src={r.thumbnail}
                  alt=""
                  style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              <div>
                <Text style={{ fontSize: 12 }}>{r.title}</Text>
                <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af' }}>
                  Código: {r.propertyCode} · Run: {r.runId}
                  {r.contactPhoneRaw && (
                    <span> · <a href={`tel:${r.contactPhoneRaw}`}>{r.contactPhoneRaw}</a></span>
                  )}
                </div>
              </div>
            </div>
          ),
        }}
      />
      {/* Create lead modal */}
      <Modal
        title={
          <Space>
            <UserPlus size={16} />
            Crear lead desde particular
          </Space>
        }
        open={!!leadModal}
        onCancel={() => { setLeadModal(null); leadForm.resetFields(); }}
        onOk={handleCreateLead}
        okText="Crear lead"
        confirmLoading={creatingLead}
        width={520}
        destroyOnClose
      >
        {leadModal && (
          <>
            {/* Property summary */}
            <div
              style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#6b7280' }}
            >
              <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>
                {leadModal.address || leadModal.title || '—'}
              </div>
              <Space wrap size={4}>
                {leadModal.price && <Tag>{leadModal.price.toLocaleString('es-ES')} €</Tag>}
                {leadModal.size && <Tag>{leadModal.size} m²</Tag>}
                {leadModal.rooms && <Tag>{leadModal.rooms} hab.</Tag>}
                {leadModal.municipality && <Tag>{leadModal.municipality}</Tag>}
                {leadModal.url && (
                  <a href={leadModal.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>
                    <ExternalLink size={11} style={{ marginRight: 2 }} />Ver en Idealista
                  </a>
                )}
              </Space>
            </div>

            <Form form={leadForm} layout="vertical">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Form.Item name="firstName" label="Nombre" rules={[{ required: true, message: 'Obligatorio' }]}>
                  <Input placeholder="Nombre" />
                </Form.Item>
                <Form.Item name="lastName" label="Apellido">
                  <Input placeholder="Apellido" />
                </Form.Item>
              </div>
              <Form.Item name="phone" label="Teléfono">
                <Input placeholder="+34 600 000 000" />
              </Form.Item>
              <Form.Item name="notes" label="Notas (pre-rellenas con datos del anuncio)">
                <Input.TextArea rows={5} />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </Spin>
  );
}
